import { LocaleLink } from "../i18n/LocaleLink";
import { Faq, PageHeading } from "../ds/core";
import PricingPlans from "../components/PricingPlans";
import PricingComparison from "../components/PricingComparison";
import { useLocale } from "../i18n/useLocale";
import type { TFunction } from "i18next";

type QA = { q: string; a: string };

const faqBe = (t: TFunction): QA[] => [
  { q: t("pricing:faq.be.minUsers.q"), a: t("pricing:faq.be.minUsers.a") },
  { q: t("pricing:faq.be.timeVsProject.q"), a: t("pricing:faq.be.timeVsProject.a") },
  { q: t("pricing:faq.be.timeZeroFree.q"), a: t("pricing:faq.be.timeZeroFree.a") },
  { q: t("pricing:faq.be.afterTrial.q"), a: t("pricing:faq.be.afterTrial.a") },
];

const faqInternational = (t: TFunction): QA[] => [
  { q: t("pricing:faq.intl.minUsers.q"), a: t("pricing:faq.intl.minUsers.a") },
  { q: t("pricing:faq.intl.timeVsProject.q"), a: t("pricing:faq.intl.timeVsProject.a") },
  { q: t("pricing:faq.intl.timeZeroFree.q"), a: t("pricing:faq.intl.timeZeroFree.a") },
  { q: t("pricing:faq.intl.afterTrial.q"), a: t("pricing:faq.intl.afterTrial.a") },
];

/* Page-local FAQ — centred, numbered, +/× accordion (white when open). Lives
   here only; the shared <Faq> component is untouched. */
function PricingFaq({ items }: { items: QA[] }) {
  const { t } = useLocale();
  return (
    <Faq
      eyebrow={
        <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1 text-[12px] font-semibold uppercase tracking-[0.16em] text-ds-ink-soft shadow-sm">
          <span className="text-ink-faint">010</span>
          <span className="h-1.5 w-1.5 rounded-full bg-ds-accent" />
          {t("pricing:faq.eyebrow")}
        </span>
      }
      title={t("pricing:faq.title")}
      items={items}
      action={
        <p className="text-ds-14 text-ds-ink-soft">
          {t("pricing:faq.moreQuestions")}{" "}
          <LocaleLink to="/contact" className="font-semibold text-ds-ink underline underline-offset-2 transition-colors hover:text-ds-accent">{t("pricing:faq.contactUs")}</LocaleLink>
        </p>
      }
    />
  );
}

export default function Pricing() {
  const { market, t } = useLocale();
  const faq = market === "belgium" ? faqBe(t) : faqInternational(t);

  return (
    <>
      {/* 1. Blue hero */}
      <PageHeading
        eyebrow={t("pricing:hero.eyebrow")}
        title={t("pricing:hero.title")}
        intro={t("pricing:hero.intro")}
      />

      {/* 2. Our pricing — same component as the home page (no heading here) */}
      <section className="bg-bg-subtle font-ds">
        <PricingPlans />
      </section>

      {/* 2b. Full feature comparison — features × plans with checkmarks (DIT-396) */}
      <PricingComparison />

      {/* 3. FAQ — page-local design */}
      <PricingFaq items={faq} />
    </>
  );
}

